import { serialize } from "@prismicio/richtext"
import type { Span, TextBlock } from "@prismicio/types-internal/lib/content"
import type { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import TTText from "@tiptap/extension-text"
import type {
  Mark as ProsemirrorMark,
  Node as ProsemirrorNode,
  Schema
} from "prosemirror-model"

import {
  Bold,
  Italic,
  Label,
  Link,
  type MarkExtensionName,
  MarkExtensions
} from "../extensions"
import type { Direction } from "../globalExtensions/TextDirection"
import { NodeUtils } from "../models/helpers/NodeUtils"
import { mergeOverlap } from "../models/Span"
import HardBreak from "./HardBreak"

const Mark = {
  fromPrismic(schema: Schema) {
    return (span: Span): ProsemirrorMark | undefined => {
      switch (span.type) {
        case "hyperlink":
          return Link.converter.fromPrismic(schema)(span)
        case "em":
          return Italic.converter.fromPrismic(schema)(span)
        case "strong":
          return Bold.converter.fromPrismic(schema)(span)
        case "label":
          return Label.converter.fromPrismic(schema)(span)
        case "list-item":
          return undefined
      }
    }
  },
  toPrismic(mark: ProsemirrorMark, start: number, end: number): Span {
    const markExtension = MarkExtensions.find(
      (extension) => mark.type.name === extension.name
    )

    if (!markExtension)
      throw new Error(`Invalid mark extension '${mark.type.name}'.`)

    switch (markExtension.name) {
      case "bold":
        return {
          type: "strong",
          start,
          end
        }
      case "em":
        return {
          type: markExtension.name,
          start,
          end
        }
      case "hyperlink":
        return {
          type: markExtension.name,
          start,
          end,
          data: markExtension.converter.toPrismic(mark)
        }
      case "label":
        return {
          type: markExtension.name,
          start,
          end,
          data: markExtension.converter.toPrismic(mark)
        }
    }
  }
}

type PrismicMark = Span["type"]

export const prismicToProsemirrorMark = {
  strong: "bold",
  label: "label",
  em: "em",
  hyperlink: "hyperlink",
  "list-item": undefined
} satisfies Record<PrismicMark, MarkExtensionName | undefined>

const component = TTText.extend({
  name: "text"
})

const converter = {
  fromPrismic(schema: Schema) {
    return function (
      extensionName: string,
      textBlock: TextBlock,
      preventHardbreaks = false
    ): ProsemirrorNode[] {
      const blockWithType: TextBlock["content"] & {
        type: string
      } = {
        type: extensionName,
        text: textBlock.content.text,
        spans: textBlock.content.spans || []
      }

      const serializedNodes = serialize(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [blockWithType as any],
        serializer(schema, preventHardbreaks)
      )

      return serializedNodes.flat()
    }
  },

  toPrismic(
    node: ProsemirrorNode,
    nodeType: RichTextNodeType,
    attributes?: { direction?: Direction }
  ): TextBlock {
    const nodeSpans = NodeUtils.children.reduce<{
      text: string
      spans: Span[]
    }>(
      node,
      (previous, child) => {
        const childText = (() => {
          if (child.type.name === HardBreak.name)
            return HardBreak.converter.toPrismic(child)
          return child.textContent
        })()
        const text = childText ? previous.text + childText : previous.text

        const start = previous.text.length
        const end = text.length

        const childSpans = (() => {
          if (!child.marks.length) return undefined
          const marksRes = NodeUtils.marks.map(child, (mark) => {
            return Mark.toPrismic(mark, start, end)
          })
          return marksRes
        })()

        return {
          ...previous,
          text,
          spans: childSpans
            ? [...previous.spans, ...childSpans]
            : previous.spans
        }
      },
      { text: "", spans: [] }
    )

    return {
      type: nodeType,
      content: {
        text: nodeSpans.text,
        spans: [...mergeOverlap(nodeSpans.spans)].sort((a, b) =>
          a.type.localeCompare(b.type)
        )
      },
      ...attributes
    }
  }
}

/**
 * Takes a singular span from the blockWithType object
 * checks whether the span has a Mark type.
 * Converts that span to a prosemirror node.
 * @param schema
 * @param preventHardbreaks
 * @returns ProsemirrorNode[]
 */
function serializer(schema: Schema, preventHardbreaks = false) {
  return (
    type: string,
    element: unknown,
    text: string | undefined,
    childNodes: ProsemirrorNode[][]
  ): ProsemirrorNode[] => {
    if (type === "span" && text && text.length) {
      if (preventHardbreaks) return [schema.text(text)]

      const textNodes = HardBreak.splitTextToNodes(
        schema,
        text
      )((text) => schema.text(text))

      return textNodes
    }

    const mark = Mark.fromPrismic(schema)(element as Span)

    if (mark) {
      const modifiedChildNodes = applyMarksToNode(mark, childNodes)

      return modifiedChildNodes
    }

    return childNodes.flat()
  }
}

function applyMarksToNode(
  mark: ProsemirrorMark,
  nestedNodes: ProsemirrorNode[][]
) {
  const newNodes = nestedNodes.flat().map((prosemirrorNode) => {
    const marks = [...prosemirrorNode.marks, mark]
    // Prosemirror sort marks by alphabetical order so it's necessary to sort them for unit tests
    const sortedMarks = marks.sort((markA, markB) =>
      markA.type.name.localeCompare(markB.type.name)
    )

    return prosemirrorNode.mark(sortedMarks)
  })

  return newNodes
}

const EditorText = {
  name: component.name,
  component,
  converter
}

// eslint-disable-next-line import/no-default-export
export default EditorText
